import { useState, useCallback } from "react";
import { useGame, BUILDING_COSTS, BuildingType, BLOCK_PRICE } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import type { SelectedBlock } from "./WorldMap";

// ─── アニメーション付きボタン ─────────────────────────────────
interface AnimBtnProps {
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
  animType?: "pop" | "bounce" | "shake";
  disabled?: boolean;
}

function AnimBtn({ onClick, className = "", children, animType = "pop", disabled }: AnimBtnProps) {
  const [anim, setAnim] = useState(false);
  const [showCoin, setShowCoin] = useState(false);

  const handleClick = useCallback(() => {
    if (disabled) return;
    setAnim(true);
    if (animType === "bounce") {
      setShowCoin(true);
      setTimeout(() => setShowCoin(false), 700);
    }
    onClick?.();
  }, [disabled, onClick, animType]);

  const animClass =
    animType === "bounce" ? "animate-btn-bounce" :
    animType === "shake"  ? "animate-shake" :
    "animate-btn-pop";

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        disabled={disabled}
        onAnimationEnd={() => setAnim(false)}
        className={`${className} ${anim ? animClass : ""} active:scale-95 transition-transform`}
      >
        {children}
      </button>
      {showCoin && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none z-50">
          <span className="animate-coin-fly text-amber-400 text-lg font-black">
            <i className="ri-coin-line"></i>
          </span>
        </div>
      )}
    </div>
  );
}

interface Props {
  block: SelectedBlock | null;
  onClose: () => void;
  onOpenBuild: () => void;
  onOpenAuction: () => void;
}

export default function BlockDetailModal({ block, onClose, onOpenBuild, onOpenAuction }: Props) {
  const { blocks, purchaseBlock } = useGame();
  const { user, openAuthModal, addOwnedBlock, spendCoins } = useAuth();
  const { lang } = useLang();
  const { isDark } = useTheme();
  const [purchasing, setPurchasing] = useState(false);
  const [msg, setMsg] = useState<{ text: string; type: "success" | "error" | "warn" } | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);

  if (!block) return null;

  const existingBlock = blocks[block.blockId];
  const isOwned = !!existingBlock?.ownerId;
  const isMyBlock = !!(user && existingBlock?.ownerId === user.id);
  const isOthersBlock = isOwned && !isMyBlock;
  const isForSale = existingBlock?.isForSale;

  const totalPrice = Math.ceil((block.areaKm2 || 1) * BLOCK_PRICE);

  const buildingIcons: Record<BuildingType, string> = {
    house: "ri-home-4-line",
    company: "ri-building-4-line",
    powerPlant: "ri-flashlight-line",
  };

  const buildingLabels: Record<BuildingType, string> = {
    house: lang === "ja" ? "住宅" : "House",
    company: lang === "ja" ? "会社" : "Company",
    powerPlant: lang === "ja" ? "発電所" : "Power Plant",
  };

  const handlePurchase = async (): Promise<void> => {
    if (!user) { openAuthModal("login"); return; }
    if (block.isChecking) {
      setMsg({ text: lang === "ja" ? "確認中です..." : "Checking...", type: "warn" });
      return;
    }
    if (block.isLand === false) {
      setMsg({ text: lang === "ja" ? "海域は購入できません" : "Cannot purchase ocean area", type: "error" });
      return;
    }
    if (isOwned) {
      setMsg({ text: lang === "ja" ? "この区画はすでに購入されています" : "This block is already owned", type: "error" });
      return;
    }
    if (user.coins < totalPrice) {
      setMsg({ text: lang === "ja" ? `コインが不足しています（必要: ${totalPrice} coins）` : `Not enough coins (need: ${totalPrice})`, type: "error" });
      return;
    }
    setPurchasing(true);
    setMsg(null);
    try {
      const result = await purchaseBlock(
        block.blockId, block.lat, block.lng,
        block.polygon, block.areaKm2,
        user.id, user.username, spendCoins
      );
      if (result.success) {
        await addOwnedBlock(block.blockId);
        setPurchaseSuccess(true);
        setMsg({ text: lang === "ja" ? `🎉 購入完了！ -${totalPrice} coins` : `🎉 Purchase complete! -${totalPrice} coins`, type: "success" });
      } else {
        setMsg({ text: result.error || (lang === "ja" ? "購入できませんでした" : "Purchase failed"), type: "error" });
      }
    } catch {
      setMsg({ text: lang === "ja" ? "購入中にエラーが発生しました" : "An error occurred during purchase", type: "error" });
    } finally {
      setPurchasing(false);
    }
  };

  const panelClass = isDark
    ? "bg-[#0f1015]/97 border-white/10 text-white"
    : "bg-white/97 border-gray-200 text-gray-900";
  const subTextClass = isDark ? "text-gray-400" : "text-gray-500";
  const dividerClass = isDark ? "border-white/5" : "border-gray-100";
  const closeClass = isDark ? "bg-white/5 hover:bg-white/10 text-gray-400" : "bg-gray-100 hover:bg-gray-200 text-gray-500";

  const renderStatus = () => {
    if (block.isChecking) {
      return (
        <span className="flex items-center gap-1.5 text-amber-400 text-xs">
          <span className="w-3 h-3 border-2 border-amber-400/40 border-t-amber-400 rounded-full animate-spin"></span>
          {lang === "ja" ? "確認中..." : "Checking..."}
        </span>
      );
    }
    if (block.isLand === false) {
      return <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 whitespace-nowrap">🌊 {lang === "ja" ? "海域" : "Ocean"}</span>;
    }
    if (isMyBlock) {
      return <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 whitespace-nowrap animate-success-burst">✅ {lang === "ja" ? "あなたの区画" : "Your block"}</span>;
    }
    if (isOthersBlock) {
      return <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 whitespace-nowrap">🔒 {lang === "ja" ? "購入済み" : "Owned"}</span>;
    }
    return <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 whitespace-nowrap">✅ {lang === "ja" ? "購入可能" : "Available"}</span>;
  };

  return (
    <div className={`absolute top-20 right-4 z-[1000] w-80 border rounded-2xl overflow-hidden shadow-2xl backdrop-blur-md animate-slide-up-fade ${panelClass}`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-5 py-4 border-b ${dividerClass}`}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-map-pin-2-line text-amber-400 text-base"></i>
          </div>
          <span className="text-sm font-bold whitespace-nowrap">
            {lang === "ja" ? "区画情報" : "Block Info"}
          </span>
        </div>
        <AnimBtn onClick={onClose} animType="pop" className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors cursor-pointer ${closeClass}`}>
          <i className="ri-close-line text-sm"></i>
        </AnimBtn>
      </div>

      <div className="p-5 space-y-4">
        {/* Info */}
        <div className="space-y-2.5">
          <div className="flex justify-between items-center">
            <span className={`text-xs whitespace-nowrap ${subTextClass}`}>{lang === "ja" ? "中心座標" : "Center"}</span>
            <span className="text-xs font-mono whitespace-nowrap">
              {block.lat.toFixed(4)}, {block.lng.toFixed(4)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className={`text-xs whitespace-nowrap ${subTextClass}`}>{lang === "ja" ? "面積" : "Area"}</span>
            <span className={`text-xs font-bold whitespace-nowrap ${block.areaKm2 >= 1 ? "text-emerald-400" : "text-red-400"}`}>
              {block.areaKm2?.toFixed(2)} km²
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className={`text-xs whitespace-nowrap ${subTextClass}`}>{lang === "ja" ? "頂点数" : "Vertices"}</span>
            <span className="text-xs whitespace-nowrap">{block.polygon?.length ?? 0}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className={`text-xs whitespace-nowrap ${subTextClass}`}>{lang === "ja" ? "ステータス" : "Status"}</span>
            {renderStatus()}
          </div>
          {!isOwned && block.isLand !== false && (
            <div className="flex justify-between items-center">
              <span className={`text-xs whitespace-nowrap ${subTextClass}`}>{lang === "ja" ? "価格" : "Price"}</span>
              <span className="text-amber-400 font-bold text-sm whitespace-nowrap">{totalPrice} coins</span>
            </div>
          )}
          {user && !isOwned && block.isLand !== false && (
            <div className="flex justify-between items-center">
              <span className={`text-xs whitespace-nowrap ${subTextClass}`}>{lang === "ja" ? "残高" : "Balance"}</span>
              <span className={`text-sm font-bold whitespace-nowrap ${user.coins >= totalPrice ? "text-emerald-400" : "text-red-400"}`}>
                {user.coins.toLocaleString()} coins
              </span>
            </div>
          )}
        </div>

        {/* Others' block */}
        {isOthersBlock && existingBlock && (
          <div className={`rounded-xl p-3 space-y-2 ${isDark ? "bg-red-500/10 border border-red-500/20" : "bg-red-50 border border-red-200"}`}>
            <div className="flex items-center gap-2">
              <i className="ri-lock-2-line text-red-400 text-sm"></i>
              <span className="text-red-400 text-xs font-semibold whitespace-nowrap">
                {lang === "ja" ? "購入済みの区画です" : "This block is already owned"}
              </span>
            </div>
            <div className={`text-xs flex items-center gap-2 ${subTextClass}`}>
              <i className="ri-user-line text-amber-400 flex-shrink-0 text-sm"></i>
              <span className="whitespace-nowrap">{lang === "ja" ? "所有者:" : "Owner:"} <strong>{existingBlock.ownerName}</strong></span>
            </div>
            <div className={`text-xs flex items-center gap-2 ${subTextClass}`}>
              <i className="ri-map-2-line text-amber-400 flex-shrink-0 text-sm"></i>
              <span className="whitespace-nowrap">{existingBlock.areaKm2?.toFixed(2)} km²</span>
            </div>
            {existingBlock.buildings.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {existingBlock.buildings.map((b, i) => (
                  <span key={i} className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${isDark ? "bg-white/10 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
                    <i className={`${buildingIcons[b.type]} text-xs`}></i>
                    {buildingLabels[b.type]}
                  </span>
                ))}
              </div>
            )}
            {isForSale && (
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse flex-shrink-0"></span>
                <span className="text-amber-400 text-xs whitespace-nowrap">
                  {lang === "ja" ? "オークション出品中！入札できます" : "On auction — you can bid!"}
                </span>
              </div>
            )}
          </div>
        )}

        {/* My block */}
        {isMyBlock && existingBlock && (
          <div className={`rounded-xl p-3 space-y-2 animate-success-burst ${isDark ? "bg-green-500/10 border border-green-500/20" : "bg-green-50 border border-green-200"}`}>
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-green-400 text-sm"></i>
              <span className="text-green-400 text-xs font-semibold whitespace-nowrap">
                {lang === "ja" ? "あなたが所有する区画です" : "You own this block"}
              </span>
            </div>
            {existingBlock.buildings.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {existingBlock.buildings.map((b, i) => (
                  <span key={i} className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${isDark ? "bg-white/10 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
                    <i className={`${buildingIcons[b.type]} text-xs`}></i>
                    {buildingLabels[b.type]}
                  </span>
                ))}
              </div>
            ) : (
              <p className={`text-xs ${subTextClass}`}>{lang === "ja" ? "まだ建物がありません" : "No buildings yet"}</p>
            )}
          </div>
        )}

        {/* Ocean warning */}
        {block.isLand === false && (
          <div className={`rounded-xl p-3 ${isDark ? "bg-blue-500/10 border border-blue-500/20" : "bg-blue-50 border border-blue-200"}`}>
            <span className="text-blue-400 text-xs font-semibold">🌊 {lang === "ja" ? "海域のため購入できません" : "Ocean area — cannot purchase"}</span>
          </div>
        )}

        {/* Feedback */}
        {msg && (
          <div className={`rounded-xl px-3 py-2.5 text-xs flex items-start gap-2 animate-success-burst ${
            msg.type === "success" ? "bg-green-500/15 text-green-400 border border-green-500/20" :
            msg.type === "error" ? "bg-red-500/15 text-red-400 border border-red-500/20" :
            "bg-amber-400/15 text-amber-400 border border-amber-400/20"
          }`}>
            <i className={`${msg.type === "success" ? "ri-checkbox-circle-line" : msg.type === "error" ? "ri-error-warning-line" : "ri-time-line"} text-sm flex-shrink-0`}></i>
            <span>{msg.text}</span>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-2">
          {!isOwned && block.isLand !== false && (
            <>
              {user && user.coins < totalPrice ? (
                <div className={`w-full rounded-xl py-3 text-sm text-center font-medium animate-shake ${isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-500"}`}>
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {lang === "ja" ? `コイン不足 (残高: ${user.coins})` : `Insufficient coins (balance: ${user.coins})`}
                </div>
              ) : (
                <AnimBtn
                  onClick={handlePurchase}
                  animType="bounce"
                  disabled={purchasing || purchaseSuccess}
                  className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 disabled:opacity-50 text-black font-bold rounded-xl py-3 text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {purchasing ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></span>
                      <span>{lang === "ja" ? "購入処理中..." : "Processing..."}</span>
                    </span>
                  ) : purchaseSuccess ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-base"></i>
                      <span>{lang === "ja" ? "購入完了！" : "Purchased!"}</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <i className="ri-shopping-cart-line text-base"></i>
                      <span>{lang === "ja" ? `この区画を購入 — ${totalPrice} coins` : `Buy This Block — ${totalPrice} coins`}</span>
                    </span>
                  )}
                </AnimBtn>
              )}
            </>
          )}

          {isMyBlock && !isForSale && (
            <div className="space-y-2">
              <AnimBtn
                onClick={onOpenBuild}
                animType="pop"
                className="w-full bg-emerald-400/10 hover:bg-emerald-400/20 border border-emerald-400/30 text-emerald-400 font-medium rounded-xl py-3 text-sm cursor-pointer whitespace-nowrap"
              >
                <i className="ri-hammer-line mr-2"></i>
                {lang === "ja" ? "建物を建てる" : "Build on this land"}
              </AnimBtn>
              <AnimBtn
                onClick={onOpenAuction}
                animType="pop"
                className="w-full bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/30 text-amber-400 font-medium rounded-xl py-3 text-sm cursor-pointer whitespace-nowrap"
              >
                <i className="ri-auction-line mr-2"></i>
                {lang === "ja" ? "オークションで売る" : "Sell via Auction"}
              </AnimBtn>
            </div>
          )}

          {isForSale && !isMyBlock && (
            <AnimBtn
              animType="bounce"
              className="w-full bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/30 text-amber-400 font-medium rounded-xl py-3 text-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-auction-line mr-2"></i>
              {lang === "ja" ? "入札する" : "Place Bid"}
            </AnimBtn>
          )}

          {!user && !isOwned && block.isLand !== false && (
            <AnimBtn
              onClick={() => openAuthModal("login")}
              animType="pop"
              className={`w-full font-medium rounded-xl py-2.5 text-xs cursor-pointer whitespace-nowrap ${isDark ? "bg-white/5 hover:bg-white/10 text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-600"}`}
            >
              <i className="ri-login-box-line mr-1"></i>
              {lang === "ja" ? "ログインして購入" : "Login to Purchase"}
            </AnimBtn>
          )}
        </div>
      </div>
    </div>
  );
}
